import React, { useContext, useState, useEffect, useMemo } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, List, ListItem, Typography } from '@mui/material';
import { LoadingButton } from "@mui/lab";

import { Utils } from "common/Utils";

import { useTheme } from '@mui/material/styles';

import { PrivateRoutesContext } from "app/PrivateRoutesContext";

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

export default function DeliverablesAndServicesAcknowledgementDialog({doShow, acknowledgement, handleSubmit, handleClose}) {
    const { userInfo } = useContext(PrivateRoutesContext);
    const [isBillingProcessAcknowledged, setIsBillingProcessAcknowledged] = useState(false);
    const [isServiceAcknowledged, setIsServiceAcknowledged] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeferring, setIsDeferring] = useState(false);

    const isAcknowledged = useMemo(() => {
        return (acknowledgement && acknowledgement.user && acknowledgement.date) !== null;
    }, [acknowledgement]);
    
    const canAcknowledge = useMemo(() => {
        return Utils.isEmployer(userInfo);
      },[userInfo]);

    useEffect(() => {
        if (!isAcknowledged) {
            setIsBillingProcessAcknowledged(false);
            setIsServiceAcknowledged(false);
        }
    },[doShow, isAcknowledged]);

    const onSubmit = () => {
        setIsSubmitting(true);

        handleSubmit(true, () => setIsSubmitting(false));
    }

    const onDefer = () => {
        setIsDeferring(true);

        handleSubmit(false, () => setIsDeferring(false));
    }

    return (
        <Dialog
            open={doShow}
            maxWidth={false}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle>
                <Typography align="center" variant="h4">
                    Client Deliverables and Services Acknowledgement
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box className="container">
                    <Box className="row">
                        <Box className="col-12">
                            <Typography variant="h5" fontWeight="600" sx={{ textDecoration: "underline" }}>
                                Acknowledgement Items:
                            </Typography>
                        </Box>
                        <Box className="col-5">
                            <Typography variant="h6" fontWeight="600">
                                Trust
                            </Typography>
                            <List
                                sx = {{
                                    listStyleType: 'disc',
                                    pl: 2,
                                    '& .MuiListItem-root': {
                                        display: 'list-item',
                                    },
                                }}
                            >
                                <ListItem>All Bona-fide plans with employer contributions must be remitted to the trust.</ListItem>
                            </List>
                        </Box>
                        <Box className="col-7">
                            <Typography variant="h6" fontWeight="600">Billing Process</Typography>
                            <List
                                sx = {{
                                    listStyleType: 'disc',
                                    pl: 2,
                                    ml: 2,
                                    '& .MuiListItem-root': {
                                        display: 'list-item',
                                    },
                                }}
                            >
                                <ListItem>Carrier Invoices</ListItem>
                                <ListItem>Once funds are remitted for this consolidated invoice, the premiums are then run through the trust and remitted by AXIM to the carriers within 14 calendar days</ListItem>
                                <ListItem>Vendor Invoices</ListItem>
                                <ListItem>Payments are then run throgh the trust and remitted by AXIM to the vendor within 30 calendar days</ListItem>
                            </List>
                        </Box>
                        <Box className="col-12">
                            <FormControlLabel 
                                control={
                                    <Checkbox
                                        checked={isBillingProcessAcknowledged || isAcknowledged}
                                        onClick={() => setIsBillingProcessAcknowledged(!isBillingProcessAcknowledged)}
                                        disabled={isAcknowledged || !canAcknowledge}
                                    />
                                }
                                label="I have received a copy of the billing process and understand it and the items detailed above"
                            />
                        </Box>
                        <Box className="col-12">
                            <Typography variant="h5" fontWeight="600" sx={{ textDecoration: "underline" }}>
                                Services:
                            </Typography>
                        </Box>
                        {acknowledgement && acknowledgement.services && acknowledgement.services.length > 0 ?
                            <>
                                <Box className="col-12" textAlign="center">
                                    <Box className="row">
                                        {acknowledgement.services.map(s => 
                                            <Box className="col border border-dark">
                                                {s.label}
                                            </Box>
                                        )}
                                    </Box>
                                    <Box className="row">
                                        {acknowledgement.services.map(s => 
                                            <Box className="col border border-dark">
                                            {s.isPurchased ? <CheckCircleIcon color="success" fontSize="large" /> : null}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <Box className="col-12">
                                    <FormControlLabel 
                                        control={
                                            <Checkbox
                                                checked={isServiceAcknowledged || isAcknowledged}
                                                onClick={() => setIsServiceAcknowledged(!isServiceAcknowledged)}
                                                disabled={isAcknowledged || !canAcknowledge}
                                            />
                                        }
                                        label="I have reviewed the marked services listed above and confirm the services reflected are accurate"
                                    />
                                </Box>
                                    {isAcknowledged ?
                                        <Box className="col-12">
                                            <Typography align="right">
                                                {`Acknowledged by ${acknowledgement.user} on ${Utils.formatDate(acknowledgement.date)}`}
                                            </Typography>
                                        </Box>
                                    : null}
                            </>
                        : null}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                {isAcknowledged || !canAcknowledge ?
                    <Button variant="outlined" color="primary" onClick={handleClose}>
                        Close
                    </Button>
                :
                    <>
                        <LoadingButton 
                            variant="outlined"
                            color="primary" 
                            onClick={onSubmit} 
                            disabled={!isBillingProcessAcknowledged || !isServiceAcknowledged || isDeferring}
                            startIcon={<CheckIcon />}
                            loading={isSubmitting}
                            loadingPosition="start"
                        >
                            Submit
                        </LoadingButton>
                        <LoadingButton
                            variant="outlined"
                            color="primary"
                            onClick={onDefer}
                            startIcon={<CloseIcon />}
                            loading={isDeferring}
                            loadingPosition="start"
                            disabled={isSubmitting}
                        >
                            Defer
                        </LoadingButton>
                    </>
                }
            </DialogActions>
        </Dialog>
    )
}